import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Modal from '../components/Modal';
import ImageProcessingService from '../components/ImageProcessingService';
import VariableService from '../components/VariableService';
import Select from 'react-select';
import AddInternalProductDialog from '../features/inventory/components/dialogs/AddInternalProductDialog';
import { fetchPlateforms as fetchAllPlateforms } from '../components/PlateformService';
import { header } from '../components/header.encry';

const statusOptions = ['pending', 'processed', 'approved', 'canceled', 'deleted'];
const imageProcessingTypeOptions = ['chatgpt', 'gemini'];
const ratioOptions = [
  { value: 'square', label: 'Square 1:1' },
  { value: 'portrait', label: 'Portrait' },
  { value: 'landscape', label: 'Landscape' },
];

const initialForm = {
  src: null,
  variable_ids: [],
  status: 'pending',
  additionnal_prompt: '',
  type: 'chatgpt',
  additionnal_data: {
    ratio: 'square',
    prompt_variables: [],
  },
};

const initialPromptVariableForm = {
  name: '',
  value: '',
};

const initialVideoRenderForm = {
  mode: 'feed',
  fit_mode: 'contain',
  audio_start_seconds: 0,
  image_offset_x: 50,
  image_offset_y: 50,
  zoom_percent: 100,
  duration_seconds: 30,
  caption: '',
  music_query: '',
  selected_music: null,
  uploaded_audio: null,
  uploaded_audio_title: '',
  uploaded_audio_artist: '',
  uploaded_audio_license_label: '',
  uploaded_audio_license_url: '',
  search_results: [],
};

const buildTrackPreviewUrl = (track) => {
  if (!track || typeof track !== 'object') {
    return '';
  }

  const directPreview =
    String(
      track.preview_url ||
      track.previewUrl ||
      track.stream_url ||
      track.streamUrl ||
      track.audio_url ||
      ''
    ).trim();

  if (!directPreview) {
    return '';
  }

  if (/\/tracks\/[^/]+\/stream$/i.test(directPreview)) {
    return `${directPreview}${directPreview.includes('?') ? '&' : '?'}preview=true`;
  }

  return directPreview;
};

const PROMPT_VARIABLE_REGEX = /{{\s*([a-zA-Z0-9_]+)(?:\|type:([a-zA-Z0-9_]+):([^}]+))?\s*}}/g;
const COLOR_FALLBACKS = {
  'iridescent blue': '#5a9bd5',
  'soft pastel pink': '#f8c8dc',
  'pastel pink': '#f8c8dc',
  'soft pink': '#f4b6c2',
};

const toTitleCaseLabel = (value) =>
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());

const normalizeHexColor = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const shortHexMatch = raw.match(/^#([0-9a-fA-F]{3})$/);
  if (shortHexMatch) {
    return `#${shortHexMatch[1]
      .split('')
      .map((char) => char + char)
      .join('')
      .toLowerCase()}`;
  }

  const fullHexMatch = raw.match(/^#([0-9a-fA-F]{6})$/);
  if (fullHexMatch) {
    return `#${fullHexMatch[1].toLowerCase()}`;
  }

  return '';
};

const resolveColorValue = (value) => {
  const normalizedHex = normalizeHexColor(value);
  if (normalizedHex) {
    return normalizedHex;
  }

  const candidate = String(value || '').trim().toLowerCase();
  if (!candidate) {
    return '#000000';
  }

  if (COLOR_FALLBACKS[candidate]) {
    return COLOR_FALLBACKS[candidate];
  }

  if (typeof document !== 'undefined') {
    const context = document.createElement('canvas').getContext('2d');
    if (context) {
      context.fillStyle = '#000000';
      context.fillStyle = candidate;
      const resolved = normalizeHexColor(context.fillStyle);
      if (resolved) {
        return resolved;
      }
    }
  }

  return '#000000';
};

const extractPromptTemplate = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed.prompt === 'string') {
      return parsed.prompt;
    }
  } catch (_error) {
    // Fall back to raw string.
  }

  return trimmed;
};

const parsePromptVariableDefinitions = (templateValue) => {
  const template = extractPromptTemplate(templateValue);
  if (!template) {
    return [];
  }

  PROMPT_VARIABLE_REGEX.lastIndex = 0;
  const fields = [];
  const seen = new Set();
  let match;

  while ((match = PROMPT_VARIABLE_REGEX.exec(template)) !== null) {
    const key = String(match[1] || '').trim();
    if (!key || seen.has(key)) {
      continue;
    }

    seen.add(key);
    const inputType = String(match[2] || 'string').trim().toLowerCase();
    const defaultValue = String(match[3] || '').trim();

    fields.push({
      key,
      label: toTitleCaseLabel(key),
      type: inputType === 'color' ? 'color' : 'string',
      default_value: defaultValue,
      value: inputType === 'color' ? resolveColorValue(defaultValue) : defaultValue,
    });
  }

  return fields;
};

const normalizePromptVariableEntries = (definitions = [], existingEntries = []) => {
  const existingMap = new Map(
    (Array.isArray(existingEntries) ? existingEntries : [])
      .filter((entry) => entry && entry.key)
      .map((entry) => [entry.key, entry])
  );

  return definitions.map((definition) => {
    const existing = existingMap.get(definition.key);
    if (!existing) {
      return definition;
    }

    return {
      ...definition,
      value:
        definition.type === 'color'
          ? resolveColorValue(existing.value || existing.default_value || definition.default_value)
          : String(existing.value ?? definition.value ?? '').trim() || definition.value,
    };
  });
};

const arePromptVariablesEqual = (left = [], right = []) =>
  JSON.stringify(left || []) === JSON.stringify(right || []);

const buildVariablePromptPlaceholder = (entry) => {
  const key = String(entry?.key || '').trim();
  if (!key) {
    return '';
  }

  const entryType = String(entry?.type || 'string').trim().toLowerCase();
  const defaultValue = String(entry?.default_value || '').trim();

  if (entryType === 'color') {
    return `{{${key}|type:color:${defaultValue || '#000000'}}}`;
  }

  if (defaultValue) {
    return `{{${key}|type:string:${defaultValue}}}`;
  }

  return `{{${key}}}`;
};

const ImageProcessingManager = () => {
  const [records, setRecords] = useState([]);
  const [formData, setFormData] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [variables, setVariables] = useState([]);
  const [compareImages, setCompareImages] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryPreview, setGalleryPreview] = useState(null);
  const [productCreationImages, setProductCreationImages] = useState([]);
  const [selectedProductCreationImage, setSelectedProductCreationImage] = useState('');
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [productDialogImage, setProductDialogImage] = useState('');
  const [productDialogGalleryImages, setProductDialogGalleryImages] = useState([]);
  const [productDialogRecordId, setProductDialogRecordId] = useState(null);
  const [productDialogRecordIds, setProductDialogRecordIds] = useState([]);
  const [productDialogInitialData, setProductDialogInitialData] = useState(null);
  const [selectedApprovedImageIds, setSelectedApprovedImageIds] = useState([]);
  const [selectedAdditionnalData, setSelectedAdditionnalData] = useState(null);
  const [selectedTextRecordId, setSelectedTextRecordId] = useState(null);
  const [resettingTextIds, setResettingTextIds] = useState([]);
  const [publishingStoryIds, setPublishingStoryIds] = useState([]);
  const [plateforms, setPlateforms] = useState([]);
  const [instagramPublishRecord, setInstagramPublishRecord] = useState(null);
  const [instagramPublishType, setInstagramPublishType] = useState('story');
  const [instagramPublishCaption, setInstagramPublishCaption] = useState('');
  const [videoRenderRecord, setVideoRenderRecord] = useState(null);
  const [videoRenderForm, setVideoRenderForm] = useState(initialVideoRenderForm);
  const [isMusicSearchLoading, setIsMusicSearchLoading] = useState(false);
  const [isVideoRendering, setIsVideoRendering] = useState(false);
  const [uploadedAudioPreviewUrl, setUploadedAudioPreviewUrl] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkQueue, setBulkQueue] = useState([]);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [promptVariableForm, setPromptVariableForm] = useState(initialPromptVariableForm);
  const [isPromptVariableSaving, setIsPromptVariableSaving] = useState(false);

  const fetchRecords = async () => {
    try {
      const data = await ImageProcessingService.listImageProcessing();
      setRecords(data);
      const totalPagesAfterFetch = Math.max(1, Math.ceil(data.length / itemsPerPage));
      setCurrentPage(prev => Math.min(prev, totalPagesAfterFetch));
      return data;
    } catch (err) {
      console.error('Error fetching image-processing records', err);
      return [];
    }
  };

  const fetchPromptVariables = async () => {
    try {
      const data = await VariableService.listVariables('prompts');
      setVariables(Array.isArray(data) ? data : []);
      return Array.isArray(data) ? data : [];
    } catch (err) {
      console.error('Error fetching variables', err);
      return [];
    }
  };

  useEffect(() => {
    fetchRecords();
    fetchPromptVariables();

    const fetchPlateformsData = async () => {
      try {
        const data = await fetchAllPlateforms();
        setPlateforms(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching plateforms', err);
      }
    };
    fetchPlateformsData();
  }, []);

  useEffect(() => {
    return () => {
      if (uploadedAudioPreviewUrl) {
        URL.revokeObjectURL(uploadedAudioPreviewUrl);
      }
    };
  }, [uploadedAudioPreviewUrl]);

  const variableMap = useMemo(() => {
    const map = {};
    variables.forEach(v => {
      map[v._id || v.id] = v.name;
    });
    return map;
  }, [variables]);

  const variableOptions = useMemo(
    () =>
      variables.map((v) => ({
        value: v._id || v.id,
        label: v.name,
      })),
    [variables]
  );

  const selectedPromptVariable = useMemo(() => {
    const selectedId = formData.variable_ids?.[0];
    if (!selectedId) {
      return null;
    }

    return variables.find((entry) => (entry._id || entry.id) === selectedId) || null;
  }, [variables, formData.variable_ids]);

  const promptVariableDefinitions = useMemo(
    () => parsePromptVariableDefinitions(selectedPromptVariable?.value),
    [selectedPromptVariable]
  );

  const promptHelperVariables = useMemo(() => {
    if (Array.isArray(formData.additionnal_data?.prompt_variables) && formData.additionnal_data.prompt_variables.length) {
      return formData.additionnal_data.prompt_variables;
    }

    return promptVariableDefinitions;
  }, [formData.additionnal_data?.prompt_variables, promptVariableDefinitions]);

  const promptHelperText = useMemo(() => {
    const placeholders = promptHelperVariables
      .map((entry) => buildVariablePromptPlaceholder(entry))
      .filter(Boolean);

    const placeholderLines = placeholders.length
      ? placeholders.map((value) => `- ${value}`).join('\n')
      : '- {{subject}}\n- {{style}}\n- {{background_color|type:color:#ffffff}}';

    const variableNotes = promptHelperVariables.length
      ? promptHelperVariables
          .map((entry) => {
            const key = String(entry?.key || '').trim();
            if (!key) return null;
            const label = String(entry?.label || key).trim();
            const type = String(entry?.type || 'string').trim().toLowerCase();
            return `- ${key}: ${label} (${type})`;
          })
          .filter(Boolean)
          .join('\n')
      : '- Add the variables you need for your final prompt template.';

    return `You are helping me create a production-ready prompt template for our Image Processing system.

Return one final prompt only, ready to be saved and used directly.

Use placeholders in the exact syntax accepted by the system:
- Basic variable: {{variable_name}}
- String variable with default value: {{variable_name|type:string:default value}}
- Color variable with default value: {{variable_name|type:color:#ffffff}}

Required variables to preserve or include:
${placeholderLines}

Current variable context:
${variableNotes}

Important rule for unknown variables or system-specific placeholders:
We accept {name|value..} please see the text bellow to adjust it

Instructions:
- Keep every placeholder intact in the final output.
- Do not replace placeholders with sample values unless they are default values inside the placeholder syntax.
- Do not return explanations, markdown fences, or JSON.
- Return a complete final prompt that is ready to work.
- If a variable meaning is unclear, keep the placeholder and add the note above in a natural place inside the prompt so the user can adjust it.`;
  }, [promptHelperVariables]);

  const getRecordAdditionnalData = (record) => {
    const source = record?.additionnal_data || record?.additional_data;
    if (!source || typeof source !== 'object') {
      return null;
    }

    const normalized = {
      name: typeof source.name === 'string' ? source.name : '',
      description: typeof source.description === 'string' ? source.description : '',
      long_description:
        typeof source.long_description === 'string' ? source.long_description : '',
      ratio:
        typeof source.ratio === 'string' &&
        ratioOptions.some((option) => option.value === source.ratio)
          ? source.ratio
          : 'square',
      prompt_variables: Array.isArray(source.prompt_variables)
        ? source.prompt_variables
            .filter((entry) => entry && typeof entry === 'object' && entry.key)
            .map((entry) => ({
              key: String(entry.key || '').trim(),
              label: String(entry.label || '').trim(),
              type: String(entry.type || 'string').trim(),
              default_value: String(entry.default_value || '').trim(),
              value: String(entry.value || '').trim(),
            }))
        : [],
    };

    return (
      normalized.name.trim() ||
      normalized.description.trim() ||
      normalized.long_description.trim() ||
      normalized.ratio.trim() ||
      normalized.prompt_variables.length
    )
      ? normalized
      : null;
  };

  const hasRecordAdditionnalData = (record) => Boolean(getRecordAdditionnalData(record));
  const getRecordRatio = (record) => getRecordAdditionnalData(record)?.ratio || 'square';
  const hasInstagramStory = (record) => Boolean(record?.instagram_story_id);
  const isPortraitApprovedRecord = (record) =>
    record?.status === 'approved' &&
    getRecordRatio(record) === 'portrait' &&
    getProcessedImageUrls(record).length > 0;

  const contentContainsHtml = (value) => /<\/?[a-z][\s\S]*>/i.test(value || '');

  const filteredRecords = useMemo(() => {
    return records.filter((record) => {
      const recordStatus = record.status || 'pending';
      const recordType = record.type || 'chatgpt';
      const matchesStatus = statusFilter === 'all' || recordStatus === statusFilter;
      const matchesType = typeFilter === 'all' || recordType === typeFilter;

      return matchesStatus && matchesType;
    });
  }, [records, statusFilter, typeFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRecords.length / itemsPerPage));
  const paginatedRecords = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredRecords.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredRecords, currentPage]);

  const selectedApprovedRecords = useMemo(() => {
    const selectedIds = new Set(selectedApprovedImageIds.map((id) => String(id)));
    return records.filter((record) => selectedIds.has(String(record?._id || record?.id || '')));
  }, [records, selectedApprovedImageIds]);

  const maxPagesToShow = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
  let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

  const openAdd = () => {
    setEditingId(null);
    setFormData(initialForm);
  };

  const resetSingleFormState = () => {
    setEditingId(null);
    setFormData(initialForm);
  };

  const toggleApprovedImageSelection = (record) => {
    const recordId = String(record?._id || record?.id || '');
    if (!recordId || record?.status !== 'approved' || !getProcessedImageUrls(record)[0]) {
      return;
    }

    setSelectedApprovedImageIds((prev) => (
      prev.includes(recordId)
        ? prev.filter((id) => id !== recordId)
        : [...prev, recordId]
    ));
  };

  const openSelectedProductCreationModal = () => {
    if (selectedApprovedProductImages.length < 2) {
      toast.error('Sélectionnez au moins deux images approuvées.');
      return;
    }

    setProductCreationImages(selectedApprovedProductImages.map((entry) => entry.imageUrl));
    setSelectedProductCreationImage(selectedApprovedProductImages[0]?.imageUrl || '');
    setProductDialogRecordId(null);
    setProductDialogRecordIds(selectedApprovedProductImages.map((entry) => String(entry.record?._id || entry.record?.id || '')).filter(Boolean));
    setProductDialogInitialData(getRecordAdditionnalData(selectedApprovedProductImages[0]?.record) || null);
    openBootstrapModal('chooseProcessedImageModal');
  };

  const resetBulkFormState = () => {
    setBulkFiles([]);
    setBulkQueue([]);
    setIsBulkProcessing(false);
  };

  const openEdit = (rec) => {
    setEditingId(rec._id);
    setFormData({
      src: null,
      variable_ids: rec.variable_ids || [],
      status: rec.status || 'pending',
      additionnal_prompt: rec.additionnal_prompt || '',
      type: rec.type || 'chatgpt',
      additionnal_data: getRecordAdditionnalData(rec) || { ratio: 'square', prompt_variables: [] },
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

const handleFileChange = (e) => {
  const file = e.target.files && e.target.files[0];
  if (!file) return;

  const forbiddenTypes = ['image/png', 'image/avif'];
  if (forbiddenTypes.includes(file.type)) {
    toast.error('Les formats PNG et AVIF ne sont pas autorisés.');
    return;
  }

  setFormData(prev => ({ ...prev, src: file }));
};

  const handleVariableChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      variable_ids: selectedOption ? [selectedOption.value] : [],
    }));
  };

  const handlePromptVariableChange = (key, value) => {
    setFormData((prev) => {
      const currentEntries = Array.isArray(prev.additionnal_data?.prompt_variables)
        ? prev.additionnal_data.prompt_variables
        : [];

      return {
        ...prev,
        additionnal_data: {
          ...(prev.additionnal_data || {}),
          prompt_variables: currentEntries.map((entry) =>
            entry.key === key ? { ...entry, value } : entry
          ),
        },
      };
    });
  };

  const handleRatioChange = (event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      additionnal_data: {
        ...(prev.additionnal_data || {}),
        ratio: value,
      },
    }));
  };

  useEffect(() => {
    setFormData((prev) => {
      const currentAdditionnalData = prev.additionnal_data || {};
      const currentEntries = Array.isArray(currentAdditionnalData.prompt_variables)
        ? currentAdditionnalData.prompt_variables
        : [];
      const nextEntries = normalizePromptVariableEntries(promptVariableDefinitions, currentEntries);

      if (arePromptVariablesEqual(currentEntries, nextEntries)) {
        return prev;
      }

      return {
        ...prev,
        additionnal_data: {
          ...currentAdditionnalData,
          prompt_variables: nextEntries,
        },
      };
    });
  }, [promptVariableDefinitions]);

  const openCompare = (rec) => {
    setCompareImages({ src: rec.src, src_processed: rec.src_processed });
  };

  const prefixUrl = useCallback((p) => {
    if (!p) return null;
    if (String(p).startsWith('http')) return p;
    return `${process.env.REACT_APP_PEGASUS_API_URL}${p}`;
  }, []);

  const openBootstrapModal = (modalId) => {
    try {
      const modalElement = document.getElementById(modalId);
      const bs = window.bootstrap;
      if (!modalElement || !bs?.Modal) return;
      const modalInstance = bs.Modal.getInstance(modalElement) || new bs.Modal(modalElement);
      modalInstance.show();
    } catch (error) {
      console.error(`Unable to open modal ${modalId}`, error);
    }
  };

  const closeBootstrapModal = (modalId) => {
    try {
      const modalElement = document.getElementById(modalId);
      const bs = window.bootstrap;
      if (!modalElement || !bs?.Modal) return;
      const modalInstance = bs.Modal.getInstance(modalElement) || new bs.Modal(modalElement);
      modalInstance.hide();
    } catch (error) {
      console.error(`Unable to close modal ${modalId}`, error);
    }
  };

  const openProductCreationDialog = (imageUrl, recordId = null, initialData = null, galleryImageUrls = []) => {
    setProductDialogImage(imageUrl || '');
    setProductDialogGalleryImages(Array.isArray(galleryImageUrls) ? galleryImageUrls.filter(Boolean) : []);
    setProductDialogRecordId(recordId || null);
    setProductDialogRecordIds(recordId ? [String(recordId)] : []);
    setProductDialogInitialData(initialData);
    setProductDialogOpen(true);
  };

  const openAdditionnalDataModal = (record) => {
    const additionnalData = getRecordAdditionnalData(record);
    if (!additionnalData) {
      toast.info('Aucune donnée additionnelle disponible.');
      return;
    }

    setSelectedAdditionnalData(additionnalData);
    setSelectedTextRecordId(record._id);
    openBootstrapModal('additionnalDataModal');
  };

  const resetGeneratedText = async (id) => {
    if (resettingTextIds.includes(id)) return;
    if (!window.confirm('Effacer le titre et les deux descriptions pour relancer Process Text ? Les images et les param\u00e8tres seront conserv\u00e9s.')) return;
    setResettingTextIds((prev) => [...prev, id]);
    try {
      const saved = await ImageProcessingService.resetImageProcessingText(id);
      setRecords((prev) => prev.map((record) => record._id === id ? saved : record));
      if (selectedTextRecordId === id) setSelectedAdditionnalData(getRecordAdditionnalData(saved));
      toast.success('Texte effac\u00e9. Relancez Process Text dans ChatGPT ou Gemini pour le r\u00e9g\u00e9n\u00e9rer.');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Impossible de r\u00e9initialiser le texte.');
    } finally {
      setResettingTextIds((prev) => prev.filter((recordId) => recordId !== id));
    }
  };

  const getProcessedImageUrls = useCallback((rec) => {
    const processedImages = Array.isArray(rec?.src_processed)
      ? rec.src_processed
      : rec?.src_processed
      ? [rec.src_processed]
      : [];

    const seen = new Set();

    return processedImages.reduce((acc, imagePath) => {
      const resolved = prefixUrl(imagePath);
      if (!resolved || seen.has(resolved)) {
        return acc;
      }
      seen.add(resolved);
      acc.push(resolved);
      return acc;
    }, []);
  }, [prefixUrl]);

  const selectedApprovedProductImages = useMemo(() => {
    const seen = new Set();

    return selectedApprovedRecords.reduce((acc, record) => {
      const processedImage = getProcessedImageUrls(record)[0];
      if (!processedImage || seen.has(processedImage)) {
        return acc;
      }

      seen.add(processedImage);
      acc.push({
        record,
        imageUrl: processedImage,
      });
      return acc;
    }, []);
  }, [selectedApprovedRecords, getProcessedImageUrls]);

  const getRecordGalleryUrls = (rec) => {
    const processedImages = getProcessedImageUrls(rec);
    const originalImage = prefixUrl(rec?.src);

    if (rec?.status === 'approved') {
      return [
        ...processedImages,
        ...(originalImage && !processedImages.includes(originalImage) ? [originalImage] : []),
      ];
    }

    return [
      ...(originalImage ? [originalImage] : []),
      ...processedImages.filter((img) => img !== originalImage),
    ];
  };

  const getPrimaryRecordPreviewUrl = (rec) => getRecordGalleryUrls(rec)[0] || null;
  const isStoryPublishing = (recordId) => publishingStoryIds.includes(recordId);
  const hasInstagramFeed = (record) => Boolean(record?.instagram_feed_id || record?.instagram_post_id);

  const getImageFileName = (imageUrl, fallbackName = 'image.jpg') => {
    const cleanUrl = String(imageUrl || '').split('?')[0];
    const candidate = cleanUrl.split('/').pop();
    return candidate && /\.[a-z0-9]+$/i.test(candidate) ? candidate : fallbackName;
  };

  const downloadImageFile = async (imageUrl, fallbackName = 'image.jpg') => {
    if (!imageUrl) {
      toast.error('Aucune image disponible pour le téléchargement.');
      return;
    }

    try {
      const response = await fetch(imageUrl, { mode: 'cors' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = getImageFileName(imageUrl, fallbackName);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Image download error', error);
      toast.error("Impossible de télécharger l'image.");
    }
  };

  const getInstagramPlatformConfig = () => {
    const instagramPlatform = (Array.isArray(plateforms) ? plateforms : []).find((plateform) => {
      const type = `${plateform?.type || ''}`.toLowerCase().trim();
      const name = `${plateform?.name || ''}`.toLowerCase().trim();
      return type === 'instagram' || name === 'instagram';
    });

    if (!instagramPlatform) {
      throw new Error('Plateforme Instagram introuvable.');
    }

    const additionnalData = instagramPlatform?.additionnal_data || {};
    const accessToken =
      additionnalData.REACT_APP_FB_TMP_KEY ||
      additionnalData.fb_tmp_key ||
      additionnalData.access_token ||
      instagramPlatform.password;
    const igUserId =
      instagramPlatform.login ||
      additionnalData.REACT_APP_FB_KEY_PEGA ||
      additionnalData.fb_key_pega ||
      additionnalData.instagram_user_id;

    if (!accessToken || !igUserId) {
      throw new Error('Configuration Instagram manquante.');
    }

    return { accessToken, igUserId };
  };

  const resetPromptVariableForm = () => {
    setPromptVariableForm(initialPromptVariableForm);
    setIsPromptVariableSaving(false);
  };

  const handlePromptVariableFormChange = (e) => {
    const { name, value } = e.target;
    setPromptVariableForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreatePromptVariable = async () => {
    const name = String(promptVariableForm.name || '').trim();
    const value = String(promptVariableForm.value || '').trim();

    if (!name) {
      toast.error('Veuillez renseigner un nom pour le prompt.');
      return;
    }

    if (!value) {
      toast.error('Veuillez renseigner la valeur du prompt.');
      return;
    }

    const data = new FormData();
    data.append('name', name);
    data.append('value', value);
    data.append('type', 'prompts');

    setIsPromptVariableSaving(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_PEGASUS_API_URL}/variables`,
        data,
        header({ 'Content-Type': 'multipart/form-data' })
      );

      const createdPrompt = response?.data;
      const createdPromptId = createdPrompt?._id || createdPrompt?.id;
      await fetchPromptVariables();

      if (createdPromptId) {
        setFormData((prev) => ({
          ...prev,
          variable_ids: [createdPromptId],
        }));
      }

      toast.success('Prompt ajoute avec succes.');
      closeBootstrapModal('promptVariableModal');
      resetPromptVariableForm();
    } catch (error) {
      console.error('Error creating prompt variable', error);
      toast.error("Erreur lors de l'enregistrement du prompt.");
    } finally {
      setIsPromptVariableSaving(false);
    }
  };

  const copyPromptHelperText = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(promptHelperText);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = promptHelperText;
        textarea.setAttribute('readonly', 'readonly');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      toast.success('Prompt helper copied.');
    } catch (error) {
      console.error('Unable to copy prompt helper text', error);
      toast.error('Unable to copy prompt helper text.');
    }
  };

  const getAvailableInstagramPublishTypes = (record) => {
    if (record?.status !== 'approved') {
      return [];
    }

    return !hasInstagramFeed(record) ? ['feed'] : [];
  };

  const resolveInstagramPublishImageUrl = (imagePath) => {
    const rawValue = String(imagePath || '').trim();
    if (!rawValue) {
      return '';
    }

    const decodedRawValue = decodeURIComponent(rawValue).trim();
    if (/^https?:\/\//i.test(decodedRawValue)) {
      return decodedRawValue;
    }

    const officeProxyMatch = rawValue.match(/image\.jpeg\.php\?path=(.+)$/i);
    if (officeProxyMatch?.[1]) {
      const decodedPath = decodeURIComponent(officeProxyMatch[1]).trim();
      if (/^https?:\/\//i.test(decodedPath)) {
        return decodedPath;
      }
      return prefixUrl(decodedPath) || '';
    }

    if (/^https?:\/\//i.test(rawValue)) {
      return rawValue;
    }

    return prefixUrl(rawValue) || '';
  };

  const openInstagramPublishModal = (record) => {
    const availableTypes = getAvailableInstagramPublishTypes(record);

    if (!availableTypes.length) {
      toast.info('Aucune publication Instagram disponible pour cette image.');
      return;
    }

    setInstagramPublishRecord(record);
    setInstagramPublishType('feed');
    setInstagramPublishCaption(
      String(
        record?.additionnal_data?.description ||
        record?.additional_data?.description ||
        ''
      ).trim()
    );
    if (typeof window !== 'undefined' && window.bootstrap) {
      const modalElement = document.getElementById('instagramPublishModal');
      if (modalElement) {
        window.bootstrap.Modal.getOrCreateInstance(modalElement).show();
      }
    }
  };

  const openVideoRenderModal = (record) => {
    const existingRender = record?.video_render || {};

    if (uploadedAudioPreviewUrl) {
      URL.revokeObjectURL(uploadedAudioPreviewUrl);
      setUploadedAudioPreviewUrl('');
    }

    setVideoRenderRecord(record);
    setVideoRenderForm({
      ...initialVideoRenderForm,
      mode: existingRender.mode || 'feed',
      fit_mode: existingRender.fit_mode || 'contain',
      audio_start_seconds: existingRender.audio_start_seconds || 0,
      image_offset_x: Number.isFinite(Number(existingRender.image_offset_x)) ? Number(existingRender.image_offset_x) : 50,
      image_offset_y: Number.isFinite(Number(existingRender.image_offset_y)) ? Number(existingRender.image_offset_y) : 50,
      zoom_percent: Number.isFinite(Number(existingRender.zoom_percent)) ? Number(existingRender.zoom_percent) : 100,
      duration_seconds: existingRender.duration_seconds || 30,
      caption: existingRender.caption || '',
      uploaded_audio_title: existingRender.audio_title || '',
      uploaded_audio_artist: existingRender.audio_artist || '',
      uploaded_audio_license_label: existingRender.license_label || '',
      uploaded_audio_license_url: existingRender.license_url || '',
    });

    openBootstrapModal('videoRenderModal');
  };

  const handleVideoRenderFormChange = (event) => {
    const { name, value } = event.target;
    setVideoRenderForm((prev) => {
      const nextValue = ['duration_seconds', 'audio_start_seconds', 'image_offset_x', 'image_offset_y', 'zoom_percent'].includes(name)
        ? Number(value)
        : value;

      if (name === 'fit_mode') {
        return {
          ...prev,
          fit_mode: nextValue,
          zoom_percent: nextValue === 'crop' ? Math.max(100, Number(prev.zoom_percent) || 100) : Number(prev.zoom_percent) || 100,
        };
      }

      if (name === 'zoom_percent' && prev.fit_mode === 'crop') {
        return {
          ...prev,
          zoom_percent: Math.max(100, Number(nextValue) || 100),
        };
      }

      return {
        ...prev,
        [name]: nextValue,
      };
    });
  };

  const handleAudioPreviewPlay = (event) => {
    if (typeof document === 'undefined') {
      return;
    }

    const currentAudio = event?.currentTarget;
    const modalElement = document.getElementById('videoRenderModal');
    if (!currentAudio || !modalElement) {
      return;
    }

    const audioElements = modalElement.querySelectorAll('audio');
    audioElements.forEach((audioElement) => {
      if (audioElement !== currentAudio && !audioElement.paused) {
        audioElement.pause();
      }
    });

    const startSeconds = Number(currentAudio.dataset?.startSeconds || 0);
    if (startSeconds > 0 && currentAudio.currentTime < startSeconds) {
      currentAudio.currentTime = startSeconds;
    }
  };

  const pauseAllVideoModalAudios = () => {
    if (typeof document === 'undefined') {
      return;
    }

    const modalElement = document.getElementById('videoRenderModal');
    if (!modalElement) {
      return;
    }

    const audioElements = modalElement.querySelectorAll('audio');
    audioElements.forEach((audioElement) => {
      if (!audioElement.paused) {
        audioElement.pause();
      }
      if (typeof audioElement.currentTime === 'number') {
        audioElement.currentTime = 0;
      }
    });
  };

  const handleSearchMusicLibrary = async () => {
    const query = String(videoRenderForm.music_query || '').trim();
    if (!query) {
      toast.error('Saisir un texte pour rechercher une musique.');
      return;
    }

    setIsMusicSearchLoading(true);
    try {
      const response = await ImageProcessingService.searchMusicLibrary(query);
      setVideoRenderForm((prev) => ({
        ...prev,
        search_results: Array.isArray(response?.results) ? response.results : [],
      }));
    } catch (error) {
      console.error('Music search error', error);
      toast.error(error?.response?.data?.error || error?.message || 'Erreur lors de la recherche de musique.');
    } finally {
      setIsMusicSearchLoading(false);
    }
  };

  const handleSubmitVideoRender = async () => {
    const recordId = videoRenderRecord?._id || videoRenderRecord?.id;
    if (!recordId) {
      toast.error('Enregistrement introuvable.');
      return;
    }

    let audioPayload = null;

    try {
      setIsVideoRendering(true);

      if (videoRenderForm.uploaded_audio) {
        const uploadResponse = await ImageProcessingService.uploadMusicAsset(videoRenderForm.uploaded_audio, {
          title: videoRenderForm.uploaded_audio_title,
          artist: videoRenderForm.uploaded_audio_artist,
          license_label: videoRenderForm.uploaded_audio_license_label,
          license_url: videoRenderForm.uploaded_audio_license_url,
        });

        audioPayload = {
          audio_source_type: 'upload',
          audio_path: uploadResponse.audio_path,
          audio_provider: uploadResponse.provider || 'internal',
          audio_title: uploadResponse.title || videoRenderForm.uploaded_audio_title,
          audio_artist: uploadResponse.artist || videoRenderForm.uploaded_audio_artist,
          license_label: uploadResponse.license_label || videoRenderForm.uploaded_audio_license_label,
          license_url: uploadResponse.license_url || videoRenderForm.uploaded_audio_license_url,
        };
      } else if (videoRenderForm.selected_music?.audio_url) {
        audioPayload = {
          audio_source_type: videoRenderForm.selected_music.source_type || 'remote',
          audio_url: videoRenderForm.selected_music.audio_url,
          audio_provider: videoRenderForm.selected_music.provider || 'audius',
          audio_title: videoRenderForm.selected_music.title || '',
          audio_artist: videoRenderForm.selected_music.artist || '',
          license_label: videoRenderForm.selected_music.license_label || '',
          license_url: videoRenderForm.selected_music.license_url || '',
        };
      }

      if (!audioPayload) {
        toast.error('Selectionner une musique ou uploader un fichier audio.');
        return;
      }

      await ImageProcessingService.renderImageProcessingVideo(recordId, {
        mode: videoRenderForm.mode,
        fit_mode: videoRenderForm.fit_mode,
        audio_start_seconds: videoRenderForm.audio_start_seconds,
        image_offset_x: videoRenderForm.image_offset_x,
        image_offset_y: videoRenderForm.image_offset_y,
        zoom_percent: videoRenderForm.zoom_percent,
        duration_seconds: videoRenderForm.duration_seconds,
        caption: videoRenderForm.caption,
        ...audioPayload,
      });

      pauseAllVideoModalAudios();
      toast.success('Video rendue avec succes.');
      const refreshedRecords = await fetchRecords();
      const refreshedRecord = refreshedRecords.find((entry) => String(entry?._id || entry?.id || '') === String(recordId));
      if (refreshedRecord) {
        setVideoRenderRecord(refreshedRecord);
      }
    } catch (error) {
      console.error('Video render error', error);
      toast.error(error?.response?.data?.error || error?.message || 'Erreur lors du rendu video.');
    } finally {
      setIsVideoRendering(false);
    }
  };

  const handlePublishInstagram = async (record, publishType, caption = '') => {
    return handlePublishInstagramMedia(record, publishType, caption, {});
  };

  const waitForInstagramContainerReady = async (creationId, accessToken) => {
    const maxAttempts = 15;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const statusResponse = await axios.get(
        `https://graph.facebook.com/v22.0/${creationId}`,
        {
          params: {
            fields: 'status_code',
          },
          headers: {
            'Authorization': `OAuth ${accessToken}`,
          },
        }
      );

      const statusCode = String(statusResponse?.data?.status_code || '').toUpperCase();
      if (!statusCode || statusCode === 'FINISHED' || statusCode === 'PUBLISHED') {
        return;
      }

      if (statusCode === 'ERROR' || statusCode === 'EXPIRED') {
        throw new Error(`Instagram media container failed with status ${statusCode}.`);
      }

      await new Promise((resolve) => setTimeout(resolve, 2000));
    }

    throw new Error('Instagram media container timed out before becoming ready.');
  };

  const handlePublishInstagramMedia = async (record, publishType, caption = '', options = {}) => {
    const recordId = record?._id || record?.id;
    const processedImagePath = Array.isArray(record?.src_processed)
      ? record.src_processed.find(Boolean)
      : record?.src_processed || '';
    const processedImageUrl = resolveInstagramPublishImageUrl(processedImagePath);
    const mediaUrlOverride = String(options?.mediaUrl || '').trim();
    const mediaKind = String(options?.mediaKind || (mediaUrlOverride ? 'video' : 'image')).trim().toLowerCase();
    const publishMediaUrl = mediaUrlOverride || processedImageUrl;

    if (!recordId) {
      toast.error('Enregistrement introuvable.');
      return;
    }

    if (record?.status !== 'approved') {
      toast.error('Seules les images approuvees peuvent etre publiees sur Instagram.');
      return;
    }

    if (!publishMediaUrl) {
      toast.error('Aucun media disponible pour la publication Instagram.');
      return;
    }

    if (publishType === 'feed' && !String(caption || '').trim()) {
      toast.error('Le caption est requis pour une publication feed.');
      return;
    }

    setPublishingStoryIds((prev) => [...prev, recordId]);

    try {
      const { accessToken, igUserId } = getInstagramPlatformConfig();
      const createUrl = `https://graph.facebook.com/v22.0/${igUserId}/media`;
      const isVideoStory = publishType === 'story' && mediaKind === 'video';
      const createPayload = publishType === 'story'
        ? (
          isVideoStory
            ? {
                media_type: 'STORIES',
                video_url: publishMediaUrl,
              }
            : {
                media_type: 'STORIES',
                image_url: publishMediaUrl,
              }
        )
        : {
            image_url: publishMediaUrl,
            caption: String(caption || '').trim(),
          };

      const createResponse = await axios.post(
        createUrl,
        createPayload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `OAuth ${accessToken}`,
          },
        }
      );

      const creationId = createResponse?.data?.id;
      if (!creationId) {
        throw new Error(`Instagram ${publishType} creation failed.`);
      }

      if (isVideoStory) {
        await waitForInstagramContainerReady(creationId, accessToken);
      } else {
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      const publishResponse = await axios.post(
        `https://graph.facebook.com/v22.0/${igUserId}/media_publish`,
        { creation_id: creationId },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `OAuth ${accessToken}`,
          },
        }
      );

      const instagramMediaId = publishResponse?.data?.id;
      if (!instagramMediaId) {
        throw new Error(`Instagram ${publishType} publish failed.`);
      }

      const updatePayload = publishType === 'story'
        ? {
            instagram_story_id: instagramMediaId,
            instagram_story_published_at: new Date().toISOString(),
          }
        : {
            instagram_feed_id: instagramMediaId,
            instagram_post_id: instagramMediaId,
            instagram_feed_caption: String(caption || '').trim(),
            instagram_feed_published_at: new Date().toISOString(),
          };

      await ImageProcessingService.updateImageProcessing(recordId, updatePayload);

      if (publishType === 'story' && record?.video_render?.output_path) {
        try {
          await ImageProcessingService.clearImageProcessingVideo(recordId);
        } catch (cleanupError) {
          console.error('Video cleanup error after Instagram story publish', cleanupError);
          toast.warning('Story publiee, mais la video locale n’a pas pu etre supprimee.');
        }
      }

      toast.success(
        publishType === 'story'
          ? 'Story Instagram publiee avec succes.'
          : 'Publication feed Instagram publiee avec succes.'
      );
      await fetchRecords();
    } catch (error) {
      console.error('Instagram publish error', error);
      toast.error(
        error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          'Erreur lors de la publication Instagram.'
      );
    } finally {
      setPublishingStoryIds((prev) => prev.filter((id) => id !== recordId));
    }
  };

  const handleSubmitInstagramPublish = async () => {
    if (!instagramPublishRecord) {
      toast.error('Enregistrement introuvable.');
      return;
    }

    await handlePublishInstagram(instagramPublishRecord, instagramPublishType, instagramPublishCaption);
    setInstagramPublishRecord(null);
    setInstagramPublishCaption('');
    setInstagramPublishType('story');
  };

  const openGallery = (rec) => {
    if (!rec) return;
    const imgs = getRecordGalleryUrls(rec);
    setGalleryImages(imgs);
    setGalleryPreview(imgs.length ? imgs[0] : null);
  };

  const handleRecharge = async () => {
    try {
      const data = await ImageProcessingService.listImageProcessing();
      setRecords(data);
      const totalPagesAfterFetch = Math.max(1, Math.ceil(data.length / itemsPerPage));
      setCurrentPage(prev => Math.min(prev, totalPagesAfterFetch));
      toast.info('Liste rechargée');
    } catch (err) {
      toast.error('Erreur lors du rechargement');
    }
  };

  const handleApprove = async (id) => {
    try {
      await ImageProcessingService.approveImageProcessing(id);
      toast.success('Image approuvée');
      await fetchRecords();
    } catch (err) {
      toast.error('Erreur lors de l\'approbation');
    }
  };

  const handleRefuse = async (id) => {
    try {
      await ImageProcessingService.refuseImageProcessing(id);
      toast.info('Image refusée');
      await fetchRecords();
    } catch (err) {
      toast.error('Erreur lors du refus');
    }
  };

  const handleCreateProduct = (rec) => {
    const processedImages = getProcessedImageUrls(rec);
    const recordId = rec?._id || rec?.id || null;
    const initialData = getRecordAdditionnalData(rec);

    if (!processedImages.length) {
      toast.error('Aucune image traitée disponible pour créer le produit.');
      return;
    }

    openProductCreationDialog(
      processedImages[0],
      recordId,
      initialData,
      processedImages.slice(1)
    );
  };

  const handleConfirmProductCreation = () => {
    if (!selectedProductCreationImage) {
      toast.error('Veuillez sélectionner une image traitée.');
      return;
    }

    closeBootstrapModal('chooseProcessedImageModal');
    const galleryImageUrls = productCreationImages.filter((imageUrl) => imageUrl !== selectedProductCreationImage);
    openProductCreationDialog(
      selectedProductCreationImage,
      productDialogRecordId,
      productDialogInitialData,
      galleryImageUrls
    );
    if (!productDialogRecordId && selectedApprovedProductImages.length) {
      setProductDialogRecordIds(
        selectedApprovedProductImages.map((entry) => String(entry.record?._id || entry.record?.id || '')).filter(Boolean)
      );
    }
    setSelectedApprovedImageIds([]);
  };

  const handleSubmit = async () => {
    if (!editingId && !formData.src) {
      toast.error('Sélectionner une image');
      return;
    }

    const payload = {
      variable_ids: formData.variable_ids,
      type: formData.type || 'chatgpt',
    };
    if (formData.src) {
      payload.src = formData.src;
    }
    if (formData.additionnal_prompt !== undefined) {
      payload.additionnal_prompt = formData.additionnal_prompt;
    }
    if (formData.additionnal_data) {
      payload.additionnal_data = formData.additionnal_data;
    }
    try {
      if (editingId) {
        payload.status = formData.status;
        await ImageProcessingService.updateImageProcessing(editingId, payload);
        toast.success('Enregistrement mis à jour');
      } else {
        payload.status = 'pending';
        await ImageProcessingService.createImageProcessing(payload);
        toast.success('Enregistrement créé');
      }
      await fetchRecords();
      closeBootstrapModal('imageProcessingModal');
      resetSingleFormState();
    } catch (err) {
      console.error('Save image-processing error', err);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Confirmer la suppression ?')) return;
    try {
      await ImageProcessingService.deleteImageProcessing(id);
      toast.success('Enregistrement supprimé');
      await fetchRecords();
      closeBootstrapModal('imageProcessingModal');
      resetSingleFormState();
    } catch (err) {
      console.error('Delete image-processing error', err);
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="container mt-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Image Processing</h2>
          <div>
            <button className="btn btn-secondary me-2" onClick={handleRecharge}>
              Recharger
            </button>
            <button
              className="btn btn-outline-dark me-2"
              type="button"
              data-bs-toggle="modal"
              data-bs-target="#promptHelperModal"
            >
              Prompt helper
            </button>
            <button
              className="btn btn-success"
              onClick={openAdd}
              data-bs-toggle="modal"
              data-bs-target="#imageProcessingModal"
            >
              Ajouter
            </button>
            <button
              className="btn btn-outline-primary ms-2"
              data-bs-toggle="modal"
              data-bs-target="#bulkImageModal"
            >Ajouter en masse</button>
            <button
              className="btn btn-outline-success ms-2"
              type="button"
              onClick={openSelectedProductCreationModal}
              disabled={selectedApprovedProductImages.length < 2}
            >
              Créer produit depuis sélection ({selectedApprovedProductImages.length})
            </button>
            {selectedApprovedImageIds.length > 0 ? (
              <button
                className="btn btn-link ms-2"
                type="button"
                onClick={() => setSelectedApprovedImageIds([])}
              >
                Vider sélection
              </button>
            ) : null}
          </div>
      </div>
      <div className="mb-3">
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">Filter by Status</label>
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">all</option>
              {statusOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label">Filter by Type</label>
            <select
              className="form-select"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">all</option>
              {imageProcessingTypeOptions.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <table className="table table-bordered">
        <thead>
          <tr>
            <th style={{ width: 48 }}>Sel.</th>
            <th>Images</th>
            <th>Variables</th>
            <th>Status</th>
            <th>Type</th>
            <th>Création</th>
            <th>Mise à jour</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedRecords.map((r) => {
            const recordId = String(r?._id || r?.id || '');
            const canSelectForMerge = r.status === 'approved' && Boolean(getProcessedImageUrls(r)[0]);
            const isSelectedForMerge = selectedApprovedImageIds.includes(recordId);

            return (
            <tr key={r._id}>
              <td className="text-center align-middle">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={isSelectedForMerge}
                  disabled={!canSelectForMerge}
                  title={canSelectForMerge ? 'Sélectionner pour créer un produit avec galerie' : 'Disponible uniquement pour les images approuvées'}
                  onChange={() => toggleApprovedImageSelection(r)}
                />
              </td>
              <td>
                <div className="d-flex align-items-center gap-2">
                  {getPrimaryRecordPreviewUrl(r) ? (
                    <img
                      src={getPrimaryRecordPreviewUrl(r)}
                      alt="Aperçu du traitement"
                      width={100}
                      className="rounded border"
                      style={{ cursor: 'pointer', objectFit: 'cover' }}
                      onClick={() => openGallery(r)}
                      data-bs-toggle="modal"
                      data-bs-target="#galleryModal"
                    />
                  ) : null}
                  <div className="d-flex align-items-center gap-1">
                    {getProcessedImageUrls(r)[0] ? (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => window.open(getProcessedImageUrls(r)[0], '_blank', 'noopener,noreferrer')}
                        title="Voir l'image principale"
                        aria-label="Voir l'image principale"
                      >
                        <i className="fa-solid fa-eye"></i>
                      </button>
                    ) : null}
                    {getRecordGalleryUrls(r).length > 1 ? (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => openGallery(r)}
                        data-bs-toggle="modal"
                        data-bs-target="#galleryModal"
                        title={`Voir la galerie (${getRecordGalleryUrls(r).length})`}
                        aria-label={`Voir la galerie (${getRecordGalleryUrls(r).length})`}
                      >
                        <i className="fa-solid fa-images"></i>
                        <span className="ms-1">{getRecordGalleryUrls(r).length}</span>
                      </button>
                    ) : null}
                  </div>
                </div>
              </td>
              <td>{(r.variable_ids || []).map(id => variableMap[id] || id).join(', ')}</td>
              <td>{r.status}</td>
              <td>{r.type || 'chatgpt'}</td>
              <td>{r.creation_date ? new Date(r.creation_date).toLocaleString() : ''}</td>
              <td>{r.update_date ? new Date(r.update_date).toLocaleString() : ''}</td>
              <td>
                <div className="d-flex flex-wrap gap-1">
                {r.status === 'processed' && (
                  <>
                    <button className="btn btn-success btn-sm" type="button" onClick={() => handleApprove(r._id)} title="Approuver" aria-label="Approuver">
                      <i className="fa-solid fa-check"></i>
                    </button>
                    <button className="btn btn-warning btn-sm" type="button" onClick={() => handleRefuse(r._id)} title="Refuser" aria-label="Refuser">
                      <i className="fa-solid fa-ban"></i>
                    </button>
                  </>
                )}
                {r.status === 'approved' && (
                  <>
                    {getProcessedImageUrls(r)[0] ? (
                      <button
                        className="btn btn-info btn-sm"
                        type="button"
                        onClick={() => downloadImageFile(getProcessedImageUrls(r)[0], `image-processing-${r._id || 'approved'}.jpg`)}
                        title="Download"
                        aria-label="Download"
                      >
                        <i className="fa-solid fa-download"></i>
                      </button>
                    ) : null}
                    <button className="btn btn-success btn-sm" type="button" onClick={() => handleCreateProduct(r)} title="Créer produit" aria-label="Créer produit">
                      <i className="fa-solid fa-box-open"></i>
                    </button>
                    <button
                      className="btn btn-outline-dark btn-sm"
                      type="button"
                      onClick={() => openVideoRenderModal(r)}
                      disabled={isVideoRendering && videoRenderRecord?._id === r._id}
                      title={r.video_render?.output_path ? 'Voir ou regenerer la video' : 'Generer une video avec musique'}
                      aria-label={r.video_render?.output_path ? 'Voir ou regenerer la video' : 'Generer une video avec musique'}
                    >
                      <i className="fa-solid fa-film"></i>
                    </button>
                    {getAvailableInstagramPublishTypes(r).length > 0 ? (
                      <button
                        className="btn btn-outline-danger btn-sm"
                        type="button"
                        onClick={() => openInstagramPublishModal(r)}
                        disabled={isStoryPublishing(r._id)}
                        title="Publier sur Instagram"
                        aria-label="Publier sur Instagram"
                      >
                        <i className="fa-brands fa-instagram"></i>
                      </button>
                    ) : null}
                    {hasInstagramStory(r) && hasInstagramFeed(r) ? (
                      <button
                        className="btn btn-outline-success btn-sm"
                        type="button"
                        disabled
                        title="Story et feed Instagram deja publies"
                        aria-label="Story et feed Instagram deja publies"
                      >
                        <i className="fa-solid fa-circle-check"></i>
                      </button>
                    ) : null}
                  </>
                )}
                {hasRecordAdditionnalData(r) && (
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    type="button"
                    onClick={() => openAdditionnalDataModal(r)}
                    title="Voir les données additionnelles"
                    aria-label="Voir les données additionnelles"
                  >
                    <i className="fa-solid fa-book-open"></i>
                  </button>
                )}
                <button
                  className="btn btn-primary btn-sm"
                  type="button"
                  onClick={() => openEdit(r)}
                  data-bs-toggle="modal"
                  data-bs-target="#imageProcessingModal"
                  title="Modifier"
                  aria-label="Modifier"
                >
                  <i className="fa-solid fa-pen-to-square"></i>
                </button>
                {(r.additionnal_data?.name || r.additionnal_data?.description || r.additionnal_data?.long_description) && (
                  <button
                    className="btn btn-outline-warning btn-sm"
                    type="button"
                    disabled={resettingTextIds.includes(r._id)}
                    onClick={() => resetGeneratedText(r._id)}
                    title="Effacer le texte pour le retraiter"
                    aria-label="Effacer le texte pour le retraiter"
                  >
                    <i className="fa-solid fa-rotate-left"></i>
                  </button>
                )}
                <button
                  className="btn btn-danger btn-sm"
                  type="button"
                  onClick={() => handleDelete(r._id)}
                  title="Supprimer"
                  aria-label="Supprimer"
                >
                  <i className="fa-solid fa-trash"></i>
                </button>
                </div>
              </td>
            </tr>
          );
          })}
        </tbody>
      </table>

      <nav className="mt-4">
        <ul className="pagination justify-content-center">
          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => setCurrentPage(1)}>
              First
            </button>
          </li>
          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>
              Previous
            </button>
          </li>
          {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map((page) => (
            <li key={page} className={`page-item ${page === currentPage ? 'active' : ''}`}>
              <button className="page-link" onClick={() => setCurrentPage(page)}>
                {page}
              </button>
            </li>
          ))}
          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>
              Next
            </button>
          </li>
          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => setCurrentPage(totalPages)}>
              Last
            </button>
          </li>
        </ul>
      </nav>

      <Modal
        modalId="promptHelperModal"
        title="Prompt Helper"
        submitLabel="Copy prompt"
        onSubmit={copyPromptHelperText}
        modalType="modal-lg"
      >
        <div className="mb-3">
          <div className="alert alert-info mb-3">
            Use this helper to ask an LLM to generate a full prompt template with variable placeholders that matches the Image Processing format.
          </div>
          {promptHelperVariables.length > 0 ? (
            <div className="mb-3">
              <div className="fw-semibold mb-2">Detected variables</div>
              <div className="d-flex flex-wrap gap-2">
                {promptHelperVariables.map((entry) => (
                  <span key={entry.key} className="badge bg-secondary">
                    {buildVariablePromptPlaceholder(entry)}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          <label className="form-label">Copyable helper prompt</label>
          <textarea
            className="form-control"
            rows={18}
            value={promptHelperText}
            readOnly
          />
        </div>
      </Modal>

      <Modal
        modalId="imageProcessingModal"
        title={editingId ? 'Modifier le traitement' : 'Ajouter un traitement'}
        submitLabel={editingId ? 'Modifier' : 'Ajouter'}
        onSubmit={handleSubmit}
        submitDisabled={formData.variable_ids.length === 0 || (!editingId && !formData.src)}
      >
        <div className="mb-3">
          <label className="form-label">Image</label>
          <input type="file" className="form-control" onChange={handleFileChange} />
        </div>
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="form-label mb-0">Variables</label>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              data-bs-toggle="modal"
              data-bs-target="#promptVariableModal"
            >
              Nouveau prompt
            </button>
          </div>
          <Select
            classNamePrefix="select"
            name="variable_ids"
            options={variableOptions}
            placeholder="Selectionner votre prompt"
            value={
              variableOptions.find(
                (option) => option.value === formData.variable_ids[0]
              ) || null
            }
            onChange={handleVariableChange}
            isClearable
            isSearchable
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Prompt additionnel</label>
          <textarea
            className="form-control"
            name="additionnal_prompt"
            value={formData.additionnal_prompt}
            onChange={handleChange}
            placeholder="Texte optionnel pour guider le traitement"
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Ratio</label>
          <select
            className="form-select"
            value={formData.additionnal_data?.ratio || 'square'}
            onChange={handleRatioChange}
          >
            {ratioOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        {formData.additionnal_data?.prompt_variables?.length ? (
          <div className="mb-3">
            <label className="form-label">Prompt variables</label>
            <div className="row g-3">
              {formData.additionnal_data.prompt_variables.map((entry) => (
                <div className="col-md-6" key={entry.key}>
                  <label className="form-label">{entry.label}</label>
                  <input
                    type={entry.type === 'color' ? 'color' : 'text'}
                    className="form-control"
                    value={entry.value || ''}
                    onChange={(event) => handlePromptVariableChange(entry.key, event.target.value)}
                  />
                  {entry.default_value ? (
                    <small className="text-muted d-block mt-1">
                      Default: {entry.default_value}
                    </small>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}
        <div className="mb-3">
          <label className="form-label">Type</label>
          <select
            className="form-select"
            name="type"
            value={formData.type}
            onChange={handleChange}
          >
            {imageProcessingTypeOptions.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        <div className="mb-3 d-flex flex-wrap">
          {variables
            .filter(
              (v) => formData.variable_ids.includes(v._id || v.id) && v.src
            )
            .map((v) => (
              <img
                key={v._id || v.id}
                src={`${process.env.REACT_APP_PEGASUS_API_URL}${v.src}`}
                width={80}
                className="me-2 mb-2"
                alt="variable"
              />
            ))}
        </div>
        {editingId && (
          <div className="mb-3">
            <label className="form-label">Status</label>
            <select
              className="form-select"
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              {statusOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        )}
      </Modal>
      
      <Modal
        modalId="promptVariableModal"
        title="Ajouter un prompt"
        submitLabel={isPromptVariableSaving ? 'Enregistrement...' : 'Ajouter'}
        onSubmit={handleCreatePromptVariable}
        onClose={resetPromptVariableForm}
        submitDisabled={isPromptVariableSaving}
      >
        <div className="mb-3">
          <label className="form-label">Nom du prompt</label>
          <input
            type="text"
            className="form-control"
            name="name"
            value={promptVariableForm.name}
            onChange={handlePromptVariableFormChange}
            placeholder="Ex: product_packshot_prompt"
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Valeur du prompt</label>
          <textarea
            className="form-control"
            name="value"
            rows={10}
            value={promptVariableForm.value}
            onChange={handlePromptVariableFormChange}
            placeholder="Entrez le prompt complet avec les placeholders acceptes par le systeme."
          />
        </div>
        <div className="alert alert-secondary mb-0">
          Type enregistre: <strong>prompts</strong>
        </div>
      </Modal>

      <Modal modalId="bulkImageModal" title="Ajouter plusieurs images" submitLabel="Démarrer" onSubmit={() => {}}>
        <div className="mb-3">
          <label className="form-label">Images (plusieurs)</label>
          <input type="file" className="form-control" onChange={(e) => {
            const files = e.target.files ? Array.from(e.target.files) : [];
            setBulkFiles(files);
            setBulkQueue(files.map(f => ({ name: f.name, status: 'queued' })));
          }} multiple />
        </div>
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="form-label mb-0">Variables</label>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              data-bs-toggle="modal"
              data-bs-target="#promptVariableModal"
            >
              Nouveau prompt
            </button>
          </div>
          <Select
            classNamePrefix="select"
            name="variable_ids"
            options={variableOptions}
            placeholder="Selectionner votre prompt"
            value={
              variableOptions.find(
                (option) => option.value === formData.variable_ids[0]
              ) || null
            }
            onChange={(opt) => setFormData(prev => ({ ...prev, variable_ids: opt ? [opt.value] : [] }))}
            isClearable
            isSearchable
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Prompt additionnel</label>
          <textarea
            className="form-control"
            name="additionnal_prompt"
            value={formData.additionnal_prompt}
            onChange={handleChange}
            placeholder="Texte optionnel pour guider le traitement"
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Ratio</label>
          <select
            className="form-select"
            value={formData.additionnal_data?.ratio || 'square'}
            onChange={handleRatioChange}
          >
            {ratioOptions.map((option) => (
              <option key={`bulk-${option.value}`} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        {formData.additionnal_data?.prompt_variables?.length ? (
          <div className="mb-3">
            <label className="form-label">Prompt variables</label>
            <div className="row g-3">
              {formData.additionnal_data.prompt_variables.map((entry) => (
                <div className="col-md-6" key={`bulk-${entry.key}`}>
                  <label className="form-label">{entry.label}</label>
                  <input
                    type={entry.type === 'color' ? 'color' : 'text'}
                    className="form-control"
                    value={entry.value || ''}
                    onChange={(event) => handlePromptVariableChange(entry.key, event.target.value)}
                  />
                  {entry.default_value ? (
                    <small className="text-muted d-block mt-1">
                      Default: {entry.default_value}
                    </small>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}
        <div className="mb-3">
          <label className="form-label">Type</label>
          <select
            className="form-select"
            name="type"
            value={formData.type}
            onChange={handleChange}
          >
            {imageProcessingTypeOptions.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <div className="fw-bold mb-2">Queue</div>
          <ul className="list-group">
            {bulkQueue.map((q, idx) => (
              <li key={idx} className="list-group-item d-flex justify-content-between align-items-center">
                <span>{q.name}</span>
                <span className={`badge ${q.status === 'done' ? 'bg-success' : q.status === 'processing' ? 'bg-primary' : q.status === 'error' ? 'bg-danger' : 'bg-secondary'}`}>{q.status}</span>
              </li>
            ))}
            {bulkQueue.length === 0 && <li className="list-group-item text-muted">Aucun fichier sélectionné</li>}
          </ul>
        </div>
        <div className="d-flex justify-content-end">
          <button className="btn btn-primary" onClick={async () => {
            if (bulkFiles.length === 0) { toast.error('Sélectionner des images'); return; }
            if (!formData.variable_ids || formData.variable_ids.length === 0) { toast.error('Sélectionner un prompt'); return; }
            setIsBulkProcessing(true);
            const queue = bulkFiles.slice();
            let hasErrors = false;
            for (let i = 0; i < queue.length; i++) {
              const file = queue[i];
              setBulkQueue(prev => prev.map((p, idx) => idx === i ? { ...p, status: 'processing' } : p));
              try {
                await ImageProcessingService.createImageProcessing({
                  src: file,
                  variable_ids: formData.variable_ids,
                  additionnal_prompt: formData.additionnal_prompt,
                  additionnal_data: formData.additionnal_data
                    ? {
                        ratio: formData.additionnal_data.ratio || 'square',
                        ...(formData.additionnal_data?.prompt_variables?.length
                          ? { prompt_variables: formData.additionnal_data.prompt_variables }
                          : {}),
                      }
                    : undefined,
                  type: formData.type || 'chatgpt',
                  status: 'pending'
                });
                setBulkQueue(prev => prev.map((p, idx) => idx === i ? { ...p, status: 'done' } : p));
              } catch (err) {
                console.error('Bulk item error', err);
                hasErrors = true;
                setBulkQueue(prev => prev.map((p, idx) => idx === i ? { ...p, status: 'error' } : p));
              }
            }
            setIsBulkProcessing(false);
            await fetchRecords();
            if (hasErrors) {
              toast.warning('Traitement termin?? avec des erreurs');
              return;
            }
            toast.success('Traitement des images termin??');
            closeBootstrapModal('bulkImageModal');
            resetBulkFormState();
            resetSingleFormState();
          }} disabled={isBulkProcessing}>
            {isBulkProcessing ? 'Traitement...' : 'Démarrer'}
          </button>
        </div>
      </Modal>
      <Modal
        modalId="compareImagesModal"
        title="Comparer les images"
        modalType="modal-xl"
      >
        {compareImages && (
          <div className="row">
            <div className="col">
              <img
                src={`${process.env.REACT_APP_PEGASUS_API_URL}${compareImages.src}`}
                alt="original"
                className="img-fluid"
              />
            </div>
            <div className="col">
              {Array.isArray(compareImages.src_processed) ? (
                compareImages.src_processed.map((img, idx) => (
                  <img
                    key={idx}
                    src={`${process.env.REACT_APP_PEGASUS_API_URL}${img}`}
                    alt="traitée"
                    className="img-fluid mb-2"
                  />
                ))
              ) : (
                <img
                  src={`${process.env.REACT_APP_PEGASUS_API_URL}${compareImages.src_processed}`}
                  alt="traitée"
                  className="img-fluid"
                />
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        modalId="videoRenderModal"
        title="Generer une video"
        modalType="modal-xl"
        submitLabel={isVideoRendering ? 'Generation...' : 'Generer'}
        onSubmit={handleSubmitVideoRender}
        submitDisabled={isVideoRendering || !videoRenderRecord}
        onClose={() => {
          if (uploadedAudioPreviewUrl) {
            URL.revokeObjectURL(uploadedAudioPreviewUrl);
            setUploadedAudioPreviewUrl('');
          }
          setVideoRenderRecord(null);
          setVideoRenderForm(initialVideoRenderForm);
        }}
      >
        {videoRenderRecord ? (
          <div className="row g-4 align-items-start">
            <div className="col-lg-7">
              <div className="d-flex flex-column gap-3">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Format video</label>
                    <select
                      className="form-select"
                      name="mode"
                      value={videoRenderForm.mode}
                      onChange={handleVideoRenderFormChange}
                    >
                      <option value="feed">Feed</option>
                      <option value="story">Story</option>
                      <option value="reel">Reel</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Adaptation image</label>
                    <select
                      className="form-select"
                      name="fit_mode"
                      value={videoRenderForm.fit_mode}
                      onChange={handleVideoRenderFormChange}
                    >
                      <option value="contain">Ajuster au format</option>
                      <option value="crop">Recadrer pour remplir</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Duree (secondes)</label>
                    <input
                      type="number"
                      min="5"
                      max="30"
                      className="form-control"
                      name="duration_seconds"
                      value={videoRenderForm.duration_seconds}
                      onChange={handleVideoRenderFormChange}
                    />
                  </div>
                </div>

                <div className="border rounded p-3">
                  <div className="fw-semibold mb-2">Position image</div>
                  <div className="small text-muted mb-3">
                    {videoRenderForm.fit_mode === 'crop'
                      ? 'Deplacez le cadrage pour choisir la zone visible.'
                      : 'Deplacez l’image dans le format pour ajuster son placement.'}
                  </div>
                  <div className="mb-3">
                    <label className="form-label d-flex justify-content-between">
                      <span>Zoom</span>
                      <span>{Math.round(Number(videoRenderForm.zoom_percent) || 0)}%</span>
                    </label>
                    <input
                      type="range"
                      min={videoRenderForm.fit_mode === 'crop' ? '100' : '50'}
                      max="200"
                      className="form-range"
                      name="zoom_percent"
                      value={videoRenderForm.zoom_percent}
                      onChange={handleVideoRenderFormChange}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label d-flex justify-content-between">
                      <span>Horizontal</span>
                      <span>{Math.round(Number(videoRenderForm.image_offset_x) || 0)}%</span>
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      className="form-range"
                      name="image_offset_x"
                      value={videoRenderForm.image_offset_x}
                      onChange={handleVideoRenderFormChange}
                    />
                  </div>
                  <div className="mb-0">
                    <label className="form-label d-flex justify-content-between">
                      <span>Vertical</span>
                      <span>{Math.round(Number(videoRenderForm.image_offset_y) || 0)}%</span>
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      className="form-range"
                      name="image_offset_y"
                      value={videoRenderForm.image_offset_y}
                      onChange={handleVideoRenderFormChange}
                    />
                  </div>
                </div>

                <div>
                  <label className="form-label">Caption interne</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    name="caption"
                    value={videoRenderForm.caption}
                    onChange={handleVideoRenderFormChange}
                    placeholder="Optionnel. Enregistre avec le rendu pour la suite du workflow."
                  />
                </div>

                <div className="border rounded p-3">
                  <div className="fw-semibold mb-2">Recherche musique libre</div>
                  <div className="input-group mb-2">
                    <input
                      type="text"
                      className="form-control"
                      name="music_query"
                      value={videoRenderForm.music_query}
                      onChange={handleVideoRenderFormChange}
                      placeholder="Ex: chill, afrobeat, ambient"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={handleSearchMusicLibrary}
                      disabled={isMusicSearchLoading}
                    >
                      {isMusicSearchLoading ? 'Recherche...' : 'Rechercher'}
                    </button>
                  </div>
                  <div className="small text-muted mb-2">
                    Les resultats viennent du provider gratuit configure au backend. Verifier vos droits avant publication finale.
                  </div>
                  <div className="list-group" style={{ maxHeight: 220, overflowY: 'auto' }}>
                    {videoRenderForm.search_results.map((track) => {
                      const isSelected = videoRenderForm.selected_music?.audio_url === track.audio_url;
                      const previewUrl = buildTrackPreviewUrl(track);
                      return (
                        <div
                          key={`${track.provider || 'music'}-${track.id}`}
                          className={`list-group-item ${isSelected ? 'active' : ''}`}
                        >
                          <div className="d-flex justify-content-between align-items-start gap-3">
                            <button
                              type="button"
                              className={`btn btn-sm ${isSelected ? 'btn-light' : 'btn-outline-primary'}`}
                              onClick={() => setVideoRenderForm((prev) => ({
                                ...prev,
                                selected_music: track,
                                uploaded_audio: null,
                                audio_start_seconds: 0,
                              }))}
                            >
                              {isSelected ? 'Selectionnee' : 'Utiliser'}
                            </button>
                            <div className="flex-grow-1">
                              <div className="fw-semibold">{track.title || 'Sans titre'}</div>
                              <div className="small mb-2">{track.artist || 'Artiste inconnu'}</div>
                              {previewUrl ? (
                                <audio
                                  controls
                                  preload="none"
                                  style={{ width: '100%' }}
                                  onPlay={handleAudioPreviewPlay}
                                  data-start-seconds={isSelected ? videoRenderForm.audio_start_seconds : 0}
                                >
                                  <source src={previewUrl} />
                                </audio>
                              ) : (
                                <div className={`small ${isSelected ? 'text-white-50' : 'text-muted'}`}>
                                  Apercu audio indisponible.
                                </div>
                              )}
                            </div>
                          </div>
                          {isSelected ? (
                            <div className="mt-3">
                              <label className="form-label d-flex justify-content-between">
                                <span>Debut de la musique</span>
                                <span>{Number(videoRenderForm.audio_start_seconds || 0).toFixed(1)}s</span>
                              </label>
                              <input
                                type="range"
                                min="0"
                                max={Math.max(0, Number(track.duration || 0))}
                                step="0.5"
                                className="form-range"
                                name="audio_start_seconds"
                                value={videoRenderForm.audio_start_seconds}
                                onChange={handleVideoRenderFormChange}
                              />
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                    {videoRenderForm.search_results.length === 0 ? (
                      <div className="text-muted small">Aucun resultat.</div>
                    ) : null}
                  </div>
                </div>

                <div className="border rounded p-3">
                  <div className="fw-semibold mb-2">Ou uploader votre audio</div>
                  <input
                    type="file"
                    className="form-control mb-2"
                    accept="audio/*"
                    onChange={(event) => {
                      const file = event.target.files?.[0] || null;
                      if (uploadedAudioPreviewUrl) {
                        URL.revokeObjectURL(uploadedAudioPreviewUrl);
                      }
                      const nextPreviewUrl = file ? URL.createObjectURL(file) : '';
                  setUploadedAudioPreviewUrl(nextPreviewUrl);
                  setVideoRenderForm((prev) => ({
                    ...prev,
                    uploaded_audio: file,
                    selected_music: file ? null : prev.selected_music,
                    audio_start_seconds: 0,
                  }));
                }}
                  />
                  {uploadedAudioPreviewUrl ? (
                    <div className="mb-3">
                      <audio controls preload="metadata" style={{ width: '100%' }} onPlay={handleAudioPreviewPlay}>
                        <source src={uploadedAudioPreviewUrl} />
                      </audio>
                    </div>
                  ) : null}
                  <div className="row g-2">
                    <div className="col-md-6">
                      <input
                        type="text"
                        className="form-control"
                        name="uploaded_audio_title"
                        value={videoRenderForm.uploaded_audio_title}
                        onChange={handleVideoRenderFormChange}
                        placeholder="Titre"
                      />
                    </div>
                    <div className="col-md-6">
                      <input
                        type="text"
                        className="form-control"
                        name="uploaded_audio_artist"
                        value={videoRenderForm.uploaded_audio_artist}
                        onChange={handleVideoRenderFormChange}
                        placeholder="Artiste"
                      />
                    </div>
                    <div className="col-md-6">
                      <input
                        type="text"
                        className="form-control"
                        name="uploaded_audio_license_label"
                        value={videoRenderForm.uploaded_audio_license_label}
                        onChange={handleVideoRenderFormChange}
                        placeholder="Licence"
                      />
                    </div>
                    <div className="col-md-6">
                      <input
                        type="text"
                        className="form-control"
                        name="uploaded_audio_license_url"
                        value={videoRenderForm.uploaded_audio_license_url}
                        onChange={handleVideoRenderFormChange}
                        placeholder="URL licence"
                      />
                    </div>
                  </div>
                </div>

                {videoRenderRecord.video_render?.output_path ? (
                  <div className="alert alert-success mb-0">
                    Derniere video:
                    {' '}
                    <a
                      href={`${process.env.REACT_APP_PEGASUS_API_URL}${videoRenderRecord.video_render.output_path}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      ouvrir le MP4
                    </a>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="col-lg-5">
              <div className="position-sticky" style={{ top: 0 }}>
                <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                  <div className="text-muted small fw-semibold">Apercu telephone</div>
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    disabled={!videoRenderRecord.video_render?.output_path || isStoryPublishing(videoRenderRecord?._id || videoRenderRecord?.id)}
                    onClick={() => handlePublishInstagramMedia(videoRenderRecord, 'story', '', {
                      mediaUrl: `${process.env.REACT_APP_PEGASUS_API_URL}${videoRenderRecord.video_render.output_path}`,
                      mediaKind: 'video'
                    })}
                    title={videoRenderRecord.video_render?.output_path ? 'Publier la video generee en story Instagram' : 'Generer une video avant publication'}
                  >
                    <i className="fa-brands fa-instagram me-1"></i>
                    {isStoryPublishing(videoRenderRecord?._id || videoRenderRecord?.id) ? 'Publication...' : 'Publier Story'}
                  </button>
                </div>
                <div
                  className="mx-auto shadow-sm"
                  style={{
                    width: '100%',
                    maxWidth: 320,
                    background: '#111827',
                    borderRadius: 32,
                    padding: 12,
                    border: '1px solid #1f2937'
                  }}
                >
                  <div
                    style={{
                      height: 24,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <div
                      style={{
                        width: 90,
                        height: 8,
                        borderRadius: 999,
                        background: '#374151'
                      }}
                    />
                  </div>
                  <div
                    style={{
                      background: '#000',
                      borderRadius: 24,
                      overflow: 'hidden',
                      aspectRatio: videoRenderForm.mode === 'feed' ? '4 / 5' : '9 / 16',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {videoRenderRecord.video_render?.output_path ? (
                      <video
                        key={videoRenderRecord.video_render.output_path}
                        controls
                        preload="metadata"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          display: 'block'
                        }}
                      >
                        <source src={`${process.env.REACT_APP_PEGASUS_API_URL}${videoRenderRecord.video_render.output_path}`} />
                      </video>
                    ) : getProcessedImageUrls(videoRenderRecord)[0] ? (
                      <img
                        src={getProcessedImageUrls(videoRenderRecord)[0]}
                        alt="Apercu video"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: videoRenderForm.fit_mode === 'crop' ? 'cover' : 'contain',
                          objectPosition: `${videoRenderForm.image_offset_x}% ${videoRenderForm.image_offset_y}%`,
                          transform: `scale(${(Number(videoRenderForm.zoom_percent) || 100) / 100})`,
                          transformOrigin: `${videoRenderForm.image_offset_x}% ${videoRenderForm.image_offset_y}%`,
                          display: 'block'
                        }}
                      />
                    ) : getPrimaryRecordPreviewUrl(videoRenderRecord) ? (
                      <img
                        src={getPrimaryRecordPreviewUrl(videoRenderRecord)}
                        alt="Apercu video"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: videoRenderForm.fit_mode === 'crop' ? 'cover' : 'contain',
                          objectPosition: `${videoRenderForm.image_offset_x}% ${videoRenderForm.image_offset_y}%`,
                          transform: `scale(${(Number(videoRenderForm.zoom_percent) || 100) / 100})`,
                          transformOrigin: `${videoRenderForm.image_offset_x}% ${videoRenderForm.image_offset_y}%`,
                          display: 'block'
                        }}
                      />
                    ) : (
                      <div className="text-center text-white-50 px-3">
                        Aucune image disponible
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="mb-0 text-muted">Aucun enregistrement selectionne.</p>
        )}
      </Modal>

      <Modal
        modalId="instagramPublishModal"
        title="Publier sur Instagram"
        submitLabel={isStoryPublishing(instagramPublishRecord?._id || instagramPublishRecord?.id) ? 'Publication...' : 'Publier'}
        onSubmit={handleSubmitInstagramPublish}
        submitDisabled={!instagramPublishRecord || (instagramPublishType === 'feed' && !String(instagramPublishCaption || '').trim()) || isStoryPublishing(instagramPublishRecord?._id || instagramPublishRecord?.id)}
        onClose={() => {
          setInstagramPublishRecord(null);
          setInstagramPublishType('story');
          setInstagramPublishCaption('');
        }}
      >
        {instagramPublishRecord ? (
          <div className="d-flex flex-column gap-3">
            <div>
              <label className="form-label">Type de publication</label>
              <select
                className="form-select"
                value={instagramPublishType}
                onChange={(e) => setInstagramPublishType(e.target.value)}
              >
                {getAvailableInstagramPublishTypes(instagramPublishRecord).includes('story') ? (
                  <option value="story">Story</option>
                ) : null}
                {getAvailableInstagramPublishTypes(instagramPublishRecord).includes('feed') ? (
                  <option value="feed">Feed</option>
                ) : null}
              </select>
            </div>
            {instagramPublishType === 'feed' ? (
              <div>
                <label className="form-label">Caption</label>
                <textarea
                  className="form-control"
                  rows={6}
                  value={instagramPublishCaption}
                  onChange={(e) => setInstagramPublishCaption(e.target.value)}
                  placeholder="Ajouter le caption du post feed"
                />
              </div>
            ) : (
              <div className="alert alert-info mb-0">
                La story sera publiee sans caption.
                {getRecordRatio(instagramPublishRecord) !== 'portrait' ? ' Instagram pourra recadrer l’image si elle n’est pas au format portrait.' : ''}
              </div>
            )}
          </div>
        ) : (
          <p className="mb-0 text-muted">Aucune publication Instagram selectionnee.</p>
        )}
      </Modal>

      <Modal
        modalId="galleryModal"
        title="Galerie d'images"
        modalType="modal-xl"
      >
        <div>
          {galleryPreview ? (
            <div className="text-center mb-3">
              <img src={galleryPreview} alt="preview" className="img-fluid" style={{ maxHeight: '60vh' }} />
            </div>
          ) : (
            <p className="text-muted">Aucune image</p>
          )}

          {galleryImages && galleryImages.length > 0 && (
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
              {galleryImages.map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt={`thumb-${idx}`}
                  width={120}
                  style={{ cursor: 'pointer', border: galleryPreview === img ? '2px solid #007bff' : '1px solid #e9ecef', borderRadius: 4 }}
                  onClick={() => setGalleryPreview(img)}
                />
              ))}
            </div>
          )}
        </div>
      </Modal>

      <Modal
        modalId="chooseProcessedImageModal"
        title="Choisir l'image principale"
        submitLabel="Créer le produit"
        onSubmit={handleConfirmProductCreation}
        submitDisabled={!selectedProductCreationImage}
        onClose={() => {
          setProductCreationImages([]);
          setSelectedProductCreationImage('');
          if (!productDialogOpen) {
            setProductDialogRecordId(null);
            setProductDialogRecordIds([]);
            setProductDialogInitialData(null);
          }
        }}
      >
        <p className="text-muted">
          Sélectionnez l&apos;image principale du produit. Toutes les autres images de la sélection seront ajoutées en galerie.
        </p>
        {productCreationImages.length > 0 ? (
          <div className="row g-3">
            {productCreationImages.map((img) => {
              const isSelected = selectedProductCreationImage === img;
              return (
                <div className="col-md-4" key={img}>
                  <button
                    type="button"
                    className={`btn w-100 p-2 ${isSelected ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setSelectedProductCreationImage(img)}
                  >
                    <img
                      src={img}
                      alt="processed choice"
                      className="img-fluid rounded mb-2"
                      style={{ maxHeight: 220, objectFit: 'contain' }}
                    />
                    <div>{isSelected ? 'Image principale' : 'Galerie si non sélectionnée'}</div>
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="mb-0 text-muted">Aucune image traitée disponible.</p>
        )}
      </Modal>
      <Modal
        modalId="additionnalDataModal"
        title="DonnÃ©es additionnelles"
        onClose={() => setSelectedAdditionnalData(null)}
      >
        {selectedAdditionnalData ? (
          <div className="d-flex flex-column gap-3">
            <div>
              <button type="button" className="btn btn-outline-warning"
                disabled={!selectedTextRecordId || resettingTextIds.includes(selectedTextRecordId)
                  || !(selectedAdditionnalData.name || selectedAdditionnalData.description || selectedAdditionnalData.long_description)}
                onClick={() => resetGeneratedText(selectedTextRecordId)}>
                {resettingTextIds.includes(selectedTextRecordId) ? 'R\u00e9initialisation...' : 'R\u00e9initialiser le texte'}
              </button>
              <div className="small text-muted mt-2">Les images sont conservées. Relancez ensuite Process Text dans ChatGPT ou Gemini.</div>
            </div>
            <div>
              <div className="fw-bold mb-1">Nom</div>
              <div>{selectedAdditionnalData.name || <span className="text-muted">Non renseignÃ©</span>}</div>
            </div>
            <div>
              <div className="fw-bold mb-1">Description</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>
                {selectedAdditionnalData.description || <span className="text-muted">Non renseignÃ©e</span>}
              </div>
            </div>
            <div>
              <div className="fw-bold mb-1">Description longue</div>
              {selectedAdditionnalData.long_description ? (
                contentContainsHtml(selectedAdditionnalData.long_description) ? (
                  <div
                    className="border rounded p-3"
                    dangerouslySetInnerHTML={{ __html: selectedAdditionnalData.long_description }}
                  />
                ) : (
                  <div className="border rounded p-3" style={{ whiteSpace: 'pre-wrap' }}>
                    {selectedAdditionnalData.long_description}
                  </div>
                )
              ) : (
                <div className="text-muted">Non renseignÃ©e</div>
              )}
            </div>
            <div>
              <div className="fw-bold mb-1">Ratio</div>
              <div>{ratioOptions.find((option) => option.value === selectedAdditionnalData.ratio)?.label || 'Square 1:1'}</div>
            </div>
          </div>
        ) : (
          <p className="mb-0 text-muted">Aucune donnÃ©e additionnelle disponible.</p>
        )}
      </Modal>
      <AddInternalProductDialog
        open={productDialogOpen}
        initialImageUrl={productDialogImage || null}
        initialGalleryImageUrls={productDialogGalleryImages}
        initialProductData={productDialogInitialData}
        onClose={() => {
          setProductDialogOpen(false);
          setProductDialogImage('');
          setProductDialogGalleryImages([]);
          setProductDialogRecordId(null);
          setProductDialogRecordIds([]);
          setProductDialogInitialData(null);
        }}
        onCreated={async () => {
          const relatedRecordIds = productDialogRecordIds.length
            ? [...productDialogRecordIds]
            : (productDialogRecordId ? [String(productDialogRecordId)] : []);
          setProductDialogOpen(false);
          setProductDialogImage('');
          setProductDialogGalleryImages([]);
          setProductDialogRecordId(null);
          setProductDialogRecordIds([]);
          setProductDialogInitialData(null);

          if (!relatedRecordIds.length) {
            return;
          }

          try {
            await Promise.all(relatedRecordIds.map((recordId) => ImageProcessingService.deleteImageProcessing(recordId)));
            const relatedRecordIdSet = new Set(relatedRecordIds.map((recordId) => String(recordId)));
            setRecords((prev) =>
              prev.filter(
                (record) => !relatedRecordIdSet.has(String(record?._id || record?.id || ''))
              )
            );
            toast.success('Produit créé et image processing supprimé.');
          } catch (error) {
            console.error('Unable to delete related image processing record', error);
            toast.warning("Produit créé, mais l'image processing n'a pas pu être supprimée.");
            await fetchRecords();
              toast.warning('Traitement terminÃ© avec des erreurs');
          }
        }}
      />
    </div>
  );
};

export default ImageProcessingManager;



